import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  DatePickerInput,
  TextInput,
  TimePickerInput,
  IcPick as icPick,
  IcTarget as icTarget,
} from '@/shared/ui';
import useEcgBookmarkTime from './useEcgBookmarkTime';

export type EcgBookmarkTimeProps = {
  ecgDataMap: any;
  timezone?: any;
};

/**
 * Renders the mark-a-flag modal for placing an ECG bookmark at a chosen timestamp. Shows read-only date and time inputs fed from the hook state with their validation errors, jump-to and pick-on-ECG-viewer icon buttons carrying tooltips, and an optional notes field capped at 200 characters submitted through onClickAddBookmark.
 *
 * @param props - Modal props forwarded to the ECG bookmark hook.
 * @returns The rendered bookmark modal body with its cancel and add buttons.
 */
const EcgBookmarkTime = ({ ecgDataMap, timezone }: EcgBookmarkTimeProps) => {
  const {
    state,
    onChange,
    onClickJumpTo,
    onClickPickOn,
    onChangeNotes,
    onKeyDownInput,
    onClickCancel,
    onClickAddBookmark,
    jumpTimeRef,
    pickTimeRef,
  } = useEcgBookmarkTime({ ecgDataMap, timezone });

  return (
    <>
      <div className="draggable-modal-content">
        <div className="content-title">Mark a flag</div>
        <div className="content">
          <div className="time-input mb-3">
            <span className="time-input-title">Timestamp</span>
            <div className="time-input-row">
              <DatePickerInput
                className="time-input-custom__date pr2"
                name="date"
                placeholder="MM/DD/YYYY"
                value={state.date}
                onChange={(date, dateString) => onChange('date', date, dateString)}
                disabled
                isError={!_.isEmpty(state.errorObject.date)}
                errorMessage={state.errorObject.date}
              />
              <TimePickerInput
                className="time-input-custom__time"
                value={state.time}
                onChange={(time, timeString) => onChange('time', time, timeString)}
                disabled
                placeholder="HH:mm:ss"
                format="HH:mm:ss"
                isError={!_.isEmpty(state.errorObject.time)}
                errorMessage={state.errorObject.time}
              />
              <div ref={jumpTimeRef}>
                <IconButton
                  id="beats-ecg-bookmark-jump-to-btn"
                  className="ms-3"
                  isFilled
                  disabled={!state.date}
                  iconComponent={<img src={icTarget} alt="Jump to" />}
                  onClick={() => {
                    onClickJumpTo(state.date);
                  }}
                />
              </div>
              <div ref={pickTimeRef}>
                <IconButton
                  id="beats-ecg-bookmark-pick-on-btn"
                  className="ms-3"
                  isFilled
                  iconComponent={<img src={icPick} alt="Pick on ECG viewer" />}
                  onClick={() => {
                    onClickPickOn();
                  }}
                />
              </div>
              <UncontrolledTooltip
                className="custom-tooltip"
                target={jumpTimeRef}
                placement="bottom"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                Jump to
              </UncontrolledTooltip>
              <UncontrolledTooltip
                className="custom-tooltip"
                target={pickTimeRef}
                placement="bottom"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                Pick on ECG viewer
              </UncontrolledTooltip>
            </div>
          </div>
          <TextInput
            label="Notes (optional)"
            name="notes"
            type="textarea"
            className="bookmark-comment"
            value={state.notes}
            placeholder="Notes..."
            onChange={onChangeNotes}
            onKeyDown={onKeyDownInput}
            id="ecg-bookmark-notes"
            maxLength={200}
          />
        </div>
      </div>
      <div className="footer-btn">
        <button id="cancel-add-bookmark" className="draggable-modal-btn" onClick={onClickCancel}>
          Cancel
        </button>
        <button
          className="draggable-modal-btn btn-main"
          onClick={onClickAddBookmark}
          id="add-bookmark"
        >
          Add Bookmark
        </button>
      </div>
    </>
  );
};

export default EcgBookmarkTime;
